import { useState, useMemo } from "react";
import {
  Flex,
  notification,
  Typography,
  Calendar,
  theme,
  Button,
  Modal,
  TimePicker,
  Tag,
  List,
  Empty,
} from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useMutation, useQuery } from "@tanstack/react-query";
import queryClient from "QueryClient/queryClient";
import { confirmSlot, editSlots } from "./apis/editSlots";
import { getSlotsByDate } from "Features/MyCalendar/apis/getSlots";
import { BookingStatus, Status } from "Constants/statusEnum";
import { OpenToMentor } from "Constants/roleEnum";
import "./styles/styles.css";
import apiClient from "QueryClient/axiosClient";

dayjs.extend(utc);
dayjs.extend(timezone);

export const EditSlots = () => {
  const { token } = theme.useToken();
  const { Title, Text } = Typography;
  const [api, contextHolder] = notification.useNotification();

  const [selectedDate, setSelectedDate] = useState(dayjs().add(1, "day"));
  const [editingSlotId, setEditingSlotId] = useState(null);
  const [editedTime, setEditedTime] = useState(null);
  const [confirmDeclineSlot, setConfirmDeclineSlot] = useState(null);
  const [viewRequestsModal, setViewRequestsModal] = useState(false);
  const [slotRequests, setSlotRequests] = useState([]);

  const { data: slotsData } = useQuery({
    queryKey: ["getSlotsByDate", selectedDate?.format("YYYY-MM-DD")],
    queryFn: () =>
      getSlotsByDate({
        start: selectedDate?.utc().toISOString(),
        end: selectedDate?.utc().add(1, "day").toISOString(),
      }),
    enabled: !!selectedDate,
  });

  const slots = useMemo(() => slotsData?.data?.slots || [], [slotsData]);

  const editSlotMutation = useMutation({
    mutationFn: editSlots,
    onSuccess: () => {
      api.success({ message: "Slot updated successfully" });
      queryClient.invalidateQueries({ queryKey: ["getSlots"] });
      queryClient.invalidateQueries({ queryKey: ["getSlotsByDate"] });
      setEditingSlotId(null);
      setEditedTime(null);
    },
    onError: () => {
      api.error({ message: "Failed to update slot" });
    },
  });

  const confirmSlotMutation = useMutation({
    mutationFn: confirmSlot,
    onSuccess: () => {
      api.success({ message: "Slot confirmed successfully" });
      queryClient.invalidateQueries({ queryKey: ["getSlots"] });
      queryClient.invalidateQueries({ queryKey: ["getSlotsByDate"] });
    },
    onError: () => {
      api.error({ message: "Failed to confirm slot" });
    },
    onSettled: () => {
      setViewRequestsModal(false);
    },
  });

  const handleDeclineClick = (slot) => {
    if (
      slot.bookingStatus === BookingStatus.BOOKED ||
      slot.bookingStatus === BookingStatus.NEW_TIME
    ) {
      setConfirmDeclineSlot(slot);
    } else {
      editSlotMutation.mutate({ slotId: slot._id, action: Status.DECLINED });
    }
  };

  const confirmDecline = () => {
    editSlotMutation.mutate({
      slotId: confirmDeclineSlot._id,
      action: Status.DECLINED,
    });
    setConfirmDeclineSlot(null);
  };

  const handleTimeChange = (time) => {
    setEditedTime(time);
  };

  const handleUpdate = (slot) => {
    if (!editedTime) return;

    const newStart = dayjs(slot.start)
      .hour(editedTime.hour())
      .minute(editedTime.minute())
      .second(0);
    const newEnd = newStart.add(30, "minute");

    editSlotMutation.mutate({
      slotId: slot._id,
      startTime: newStart.utc().toISOString(),
      endTime: newEnd.utc().toISOString(),
      action: Status.NEW_TIME,
    });
  };

  const handleViewRequests = async (slot) => {
    try {
      setViewRequestsModal(true);
      const data = await apiClient.get(
        `/oneGradSlot/getSlotRequests/${slot._id}`
      );
      setSlotRequests(data?.data?.requests);
    } catch (err) {
      console.log(err);
      api.error({ message: "Failed to get slot requests" });
      setViewRequestsModal(false);
    }
  };

  const wrapperStyle = {
    width: 300,
    border: `1px solid ${token.colorBorderSecondary}`,
    borderRadius: token.borderRadiusLG,
    flex: 1,
  };

  const disablePastDates = (current) =>
    current && current < dayjs().add(1, "day").startOf("day");

  return (
    <Flex vertical justify="top" align="center" style={{ height: "100%" }}>
      {contextHolder}
      <Title level={3}>Please select a date to view and edit your slots.</Title>
      <Flex
        justify="space-evenly"
        align="flex-start"
        style={{ width: "100%", marginTop: "2em" }}
        wrap
      >
        <div style={wrapperStyle} className="appointments-item">
          <Calendar
            fullscreen={false}
            onSelect={(value) => setSelectedDate(value.startOf("day"))}
            value={selectedDate}
            disabledDate={disablePastDates}
          />
        </div>

        <div style={{ flex: 1, margin: "1em 2em" }}>
          {slots.map((slot) => {
            return slot?.status !== Status.DECLINED ? (
              <div
                key={slot._id}
                style={{
                  marginBottom: "1em",
                  padding: "1em",
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                }}
              >
                <Text strong>{slot.name || OpenToMentor}</Text>
                <p>
                  {dayjs.utc(slot.start).local().format("HH:mm")} -{" "}
                  {dayjs.utc(slot.end).local().format("HH:mm")}
                </p>
                <Flex gap="small" justify="space-between">
                  <div>
                    <Button
                      className="action-button"
                      type="primary"
                      onClick={() => handleViewRequests(slot)}
                      disabled={
                        slot?.bookingStatus === BookingStatus.BOOKED ||
                        slot?.bookingStatus === BookingStatus.CHECKEDOUT
                      }
                    >
                      View Requests
                    </Button>
                    <Button
                      className="action-button"
                      danger
                      onClick={() => handleDeclineClick(slot)}
                      loading={editSlotMutation.isLoading}
                      disabled={
                        slot?.bookingStatus === BookingStatus.CHECKEDOUT
                      }
                    >
                      Decline
                    </Button>
                  </div>
                  <div>
                    {slot?.bookingStatus === BookingStatus.BOOKED && (
                      <Tag color="red">Booked by user</Tag>
                    )}
                    {slot?.bookingStatus === BookingStatus.CHECKEDOUT && (
                      <Tag color="orange">Awaiting payment from user</Tag>
                    )}
                  </div>
                </Flex>

                {editingSlotId === slot._id && (
                  <Flex style={{ marginTop: "1em" }} justify="space-between">
                    <TimePicker
                      format="HH:mm"
                      onChange={handleTimeChange}
                      placeholder="Select new time"
                    />
                    <Button
                      type="link"
                      onClick={() => handleUpdate(slot)}
                      disabled={!editedTime}
                      loading={editSlotMutation.isLoading}
                    >
                      Save
                    </Button>
                  </Flex>
                )}
              </div>
            ) : (
              <></>
            );
          })}
        </div>
      </Flex>

      <Modal
        title="Confirm Decline"
        open={confirmDeclineSlot}
        onOk={confirmDecline}
        onCancel={() => setConfirmDeclineSlot(null)}
        okText="Yes, Decline"
        cancelText="Cancel"
        confirmLoading={editSlotMutation.isLoading}
      >
        <p>
          This slot is currently booked. Are you sure you want to decline it?
        </p>
      </Modal>

      <Modal
        open={viewRequestsModal}
        footer={null}
        onCancel={() => setViewRequestsModal(false)}
        title="Requests for this slot"
      >
        {slotRequests.length === 0 ? (
          <Empty description="No pending requests found" />
        ) : (
          <List
            dataSource={slotRequests}
            renderItem={(item, index) => (
              <List.Item
                key={index}
                className="group flex items-center justify-between"
              >
                <div className="flex flex-col gap-1">
                  <span>
                    <span className="font-bold">Name:</span> {item.name || "—"}
                  </span>

                  <span>
                    <span className="font-bold">Country:</span>{" "}
                    {item.country || "—"}
                  </span>

                  <span>
                    <span className="font-bold">Program:</span>{" "}
                    {item.applyingProgram || "—"}
                  </span>

                  <span>
                    <span className="font-bold">Reason to meet:</span>{" "}
                    {item.reason}
                  </span>
                </div>

                <Button
                  type="primary"
                  size="small"
                  className="
                      opacity-0
                      pointer-events-none
                      transition-opacity
                      duration-200
                      group-hover:opacity-100
                      group-hover:pointer-events-auto
                      !bg-[#80E280]
                  "
                  onClick={() => {
                    confirmSlotMutation.mutate({
                      slotId: item.slotId,
                      slotRequestId: item._id,
                      user_id: item.attendeeId,
                    });
                  }}
                >
                  <p className="!text-black">Confirm</p>
                </Button>
              </List.Item>
            )}
          />
        )}
      </Modal>
    </Flex>
  );
};
